/**
 * Debt payoff - w2_17 (Worthly 2.0, Premium, in Your plans).
 *
 * Set up once: pick your debts, add what you owe today (no future interest),
 * the yearly rate and the minimum payment, your extra each month, and an
 * order (avalanche: highest interest first, snowball: smallest first).
 * Then each month you review and confirm each payment around its payment
 * day. When a debt is paid off, we ask whether its freed-up minimum moves
 * on to the next one. All the maths is in utils/debtPayoff.js.
 *
 * w2_19: a debt can be paid monthly, every 2 weeks, every week or twice a month.
 *
 * Saved as userData.debt_payoff = { debts, extra, method, rollover,
 *   payments, skipped, history, started }
 */
import { useEffect, useMemo, useState } from "react";
import confetti from "canvas-confetti";
import {
  K, tintAt, num, toText, money, wholeMoney, dayKey, parseDay, shortDate, monthYear,
  PlanPage, PlanSheet, Label, Help, MoneyInput, BlackButton, QuietButton, Chip, Card, Note, Bar, EmojiSquare, inputStyle,
} from "./planKit";
import { simulate, monthAt, splitPayment, minTooLow } from "../../utils/debtPayoff";

const FLAG = '\u{1F3C1}';
export const debtEmoji = (name) => {
  const t = String(name || '').toLowerCase();
  const pairs = [
    [/card|visa|amex|mastercard|credit/, '\u{1F4B3}'], [/car|auto|vehicle/, '\u{1F697}'], [/student|osap|school|tuition/, '\u{1F393}'],
    [/mortgage|home|house|condo/, '\u{1F3E0}'], [/line of credit|loc|heloc|bank/, '\u{1F3E6}'], [/phone/, '\u{1F4F1}'],
    [/medical|dental|hospital/, '\u{1F3E5}'], [/family|friend|mum|mom|dad/, '\u{1F91D}'],
  ];
  const hit = pairs.find(([re]) => re.test(t));
  return hit ? hit[1] : '\u{1F9FE}';
};
const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const r2 = (v) => Math.round(num(v) * 100) / 100;
const monthKey = (d) => dayKey(d).slice(0, 7);
const ordinal = (dd) => { const v = dd % 100; const s = (v >= 11 && v <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[dd % 10] || 'th'); return dd + s; };
// How often a debt is paid. The minimum is per payment; the plan works in months.
export const FREQS = [['monthly', 'Monthly'], ['biweekly', 'Every 2 weeks'], ['weekly', 'Every week'], ['semimonthly', 'Twice a month']];
const perMonth = (f) => ({ biweekly: 26 / 12, weekly: 52 / 12, semimonthly: 2 }[f] || 1);
export const monthlyMin = (d) => num(d.min) * perMonth(d.freq);
const everyWord = (f) => ({ biweekly: 'every 2 weeks', weekly: 'a week', semimonthly: 'twice a month' }[f] || 'a month');
const eachWord = (f) => ({ biweekly: 'every 2 weeks', weekly: 'each week', semimonthly: 'each time', monthly: 'each month' }[f] || 'each month');
const dayAt = (y, m, dd) => { const last = new Date(y, m + 1, 0).getDate(); return new Date(y, m, Math.min(Math.max(1, Math.round(num(dd) || 1)), last), 12); };
/** Every payment date for a debt between two dates (both included). */
export const dueDates = (d, from, to) => {
  const out = [];
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 12);
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 12);
  const f = d.freq || 'monthly';
  if (f === 'weekly' || f === 'biweekly') {
    const step = f === 'weekly' ? 7 : 14;
    const anchor = d.next_date ? parseDay(d.next_date) : a;
    const k0 = Math.floor((a - anchor) / (step * 86400000)) - 1;
    for (let k = k0; ; k += 1) {
      const x = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + k * step, 12);
      if (x > b) break;
      if (x >= a) out.push(x);
    }
    return out;
  }
  for (let y = a.getFullYear(), m = a.getMonth(); new Date(y, m, 1) <= b; m += 1) {
    const days = f === 'semimonthly' ? [d.pay_day || 1, d.pay_day2 || 15] : [d.pay_day || 1];
    days.forEach((dd) => { const x = dayAt(y, m, dd); if (x >= a && x <= b) out.push(x); });
    if (m >= 11) { m = -1; y += 1; }
  }
  return out.sort((x, z) => x - z);
};
/** "The 12th of each month", "Every 2 weeks · next Nov 6", "The 1st and 15th". */
const scheduleText = (d) => {
  const f = d.freq || 'monthly';
  if (f === 'semimonthly') return `The ${ordinal(Math.round(num(d.pay_day) || 1))} and ${ordinal(Math.round(num(d.pay_day2) || 15))}`;
  if (f === 'weekly' || f === 'biweekly') {
    const t = new Date();
    const nxt = dueDates(d, t, new Date(t.getFullYear(), t.getMonth(), t.getDate() + 15))[0];
    return `${f === 'weekly' ? 'Every week' : 'Every 2 weeks'}${nxt ? ' \u00B7 next ' + shortDate(nxt) : ''}`;
  }
  return `The ${ordinal(Math.round(num(d.pay_day) || 1))} of each month`;
};
const isPaid = (data, d, due) => {
  const key = dayKey(due);
  return (data.payments || []).some((p) => p.debt_id === d.id && (p.due ? p.due === key : (d.freq || 'monthly') === 'monthly' && String(p.date).slice(0, 7) === key.slice(0, 7)))
    || (data.skipped || []).some((x) => x.debt_id === d.id && (x.due ? x.due === key : x.month === key.slice(0, 7)));
};
/** What one payment should be: this month's plan amount, split over the payments in a month. */
const perPayment = (sim, d) => r2((sim.firstMonthPay[d.id] || monthlyMin(d)) / perMonth(d.freq));

const METHODS = {
  avalanche: { title: 'Highest interest first', emoji: '\u{1F3D4}️', name: 'The avalanche method', what: 'You pay the least interest overall, but your first debt can take longer to clear.' },
  snowball: { title: 'Smallest balance first', emoji: '⛄', name: 'The snowball method', what: 'You pay off whole debts sooner, so you get small wins quickly. Lots of people find that keeps them motivated, even if it costs a little more interest.' },
};

/**
 * Month 1 of the plan: this month if a payment is still to come this month
 * (on or after the day you started), otherwise next month.
 */
export const planBase = (debts, started, payments = [], skipped = [], today = new Date()) => {
  const startKey = started || dayKey(today);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0, 12);
  const data = { payments, skipped };
  const pending = (debts || []).filter((d) => num(d.balance) > 0.004).some((d) => (
    dueDates(d, today, monthEnd).some((due) => dayKey(due) >= startKey && !isPaid(data, d, due))
  ));
  return new Date(today.getFullYear(), today.getMonth() + (pending ? 0 : 1), 1, 12);
};

/** The plan for a set of debts. Freed minimums of debts already paid off are part of the extra. */
const planFor = (data, overrides = {}) => {
  const debts = (overrides.debts || data.debts || []);
  const rollover = overrides.rollover || data.rollover || {};
  const active = debts.filter((d) => num(d.balance) > 0.004);
  const freed = debts.filter((d) => num(d.balance) <= 0.004 && rollover[d.id] !== false).reduce((s, d) => s + monthlyMin(d), 0);
  const extra = num(overrides.extra != null ? overrides.extra : data.extra);
  const sim = simulate({
    debts: active.map((d) => ({ id: d.id, balance: num(d.balance), rate: num(d.rate), min: monthlyMin(d) })),
    extra: extra + freed,
    method: overrides.method || data.method || 'avalanche',
    rollover,
  });
  sim.base = planBase(debts, data.started, data.payments, data.skipped);
  return sim;
};

/** "$420, then $610 from Jan 2028" for one debt in a plan. */
const paymentStory = (sim, id) => {
  const pays = sim.payments.map((p) => r2(p[id] || 0));
  if (!pays.length || !pays[0]) return '';
  const first = pays[0];
  const done = sim.months[id];
  let up = -1;
  for (let i = 1; i < pays.length && (done == null || i < done - 1); i += 1) { if (pays[i] > first + 1) { up = i; break; } }
  // The month a debt before it is cleared, only part of its payment moves over. Show the full amount from the month after.
  if (up >= 0 && up + 1 < pays.length && (done == null || up + 1 < done - 1) && pays[up + 1] > pays[up] + 1) up += 1;
  return up < 0 ? `${wholeMoney(first)} a month` : `${wholeMoney(first)} a month, then ${wholeMoney(pays[up])} from ${monthYear(monthAt(up + 1, sim.base))}`;
};

export default function DebtPayoff({ data: rawData, wealthDebts = [], onSave: saveUp, onBack }) {
  // What you just saved shows straight away, before the save comes back.
  const [local, setLocal] = useState(null);
  const data = local || rawData || {};
  const onSave = (next) => { setLocal(next); saveUp(next); };
  const debts = data.debts || [];
  const [setup, setSetup] = useState(!debts.length);
  const [page, setPage] = useState(null); // null | 'whatif' | debt id
  const [review, setReview] = useState(null); // { debt, amount, due }
  const [addOpen, setAddOpen] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false); // w2_20
  const [party, setParty] = useState(null); // id of a debt just paid off

  const save = (patch, note) => {
    const next = { ...data, ...patch };
    const ds = next.debts || [];
    const total = r2(ds.reduce((s, d) => s + Math.max(0, num(d.balance)), 0));
    const hist = (next.history || []).slice(-239);
    if (note !== 'nohistory') {
      const today = dayKey(new Date());
      if (hist.length && hist[hist.length - 1].d === today) hist[hist.length - 1] = { d: today, total };
      else hist.push({ d: today, total });
    }
    next.history = hist;
    onSave(next);
  };

  if (setup) {
    return (
      <Setup
        wealthDebts={wealthDebts}
        onBack={() => (debts.length ? setSetup(false) : onBack())}
        onFinish={(plan) => {
          const total = r2(plan.debts.reduce((s, d) => s + num(d.balance), 0));
          onSave({ ...plan, payments: [], skipped: [], rollover: {}, started: dayKey(new Date()), history: [{ d: dayKey(new Date()), total }] });
          setSetup(false);
        }}
      />
    );
  }

  const sim = planFor(data);
  const active = debts.filter((d) => num(d.balance) > 0.004);
  const confirmPayment = (debt, amount, dateKey) => {
    const split = splitPayment(debt.balance, debt.rate, amount);
    const payment = { id: newId(), debt_id: debt.id, amount: r2(amount), date: dateKey, due: review && review.due ? dayKey(review.due) : null, interest: split.interest, principal: split.principal };
    const paidOff = split.left <= 0.004;
    const nextDebts = debts.map((d) => (d.id === debt.id ? { ...d, balance: split.left, ...(paidOff ? { paid_on: dateKey } : {}) } : d));
    save({ debts: nextDebts, payments: [...(data.payments || []), payment] });
    setReview(null);
    if (paidOff) setParty(debt.id);
  };
  const skipMonth = (debt) => {
    const due = review && review.due ? review.due : new Date();
    save({ skipped: [...(data.skipped || []), { debt_id: debt.id, due: dayKey(due), month: monthKey(due) }] }, 'nohistory');
    setReview(null);
  };

  if (page === 'whatif') return <WhatIf data={data} onBack={() => setPage(null)} onSave={(extra) => { save({ extra }, 'nohistory'); setPage(null); }} />;
  const openDebt = debts.find((d) => d.id === page);
  if (openDebt) {
    return (
      <DebtPage
        debt={openDebt}
        index={debts.indexOf(openDebt)}
        data={data}
        sim={sim}
        onBack={() => setPage(null)}
        onSaveDebt={(d) => save({ debts: debts.map((x) => (x.id === d.id ? d : x)) })}
        onDelete={() => { save({ debts: debts.filter((x) => x.id !== openDebt.id), payments: (data.payments || []).filter((p) => p.debt_id !== openDebt.id) }); setPage(null); }}
        onReview={(d) => {
          const t = new Date();
          const due = dueDates(d, new Date(t.getFullYear(), t.getMonth(), t.getDate() - 35), new Date(t.getFullYear(), t.getMonth(), t.getDate() + 20)).find((x) => !isPaid(data, d, x)) || t;
          setReview({ debt: d, amount: perPayment(sim, d), due });
        }}
        reviewSheet={review && (
          <ReviewSheet review={review} onClose={() => setReview(null)} onConfirm={confirmPayment} onSkip={skipMonth} />
        )}
      />
    );
  }

  // Payments to review: every payment date from the last five weeks up to three days
  // ahead, from the day you started, that isn't confirmed or skipped yet.
  const today = new Date();
  const mk = monthKey(today);
  const winFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 35);
  const winTo = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3);
  const reviewList = [];
  active.forEach((d) => {
    dueDates(d, winFrom, winTo).forEach((due) => {
      if (data.started && dayKey(due) < data.started) return;
      if (!isPaid(data, d, due)) reviewList.push({ d, due });
    });
  });
  reviewList.sort((a, b) => a.due - b.due);
  const paidThisMonth = active.filter((d) => (data.payments || []).some((p) => p.debt_id === d.id && String(p.date).slice(0, 7) === mk)).map((d) => ({ d }));

  const leftTotal = active.reduce((s, d) => s + num(d.balance), 0);
  const paidOff = debts.reduce((s, d) => s + Math.max(0, num(d.start_balance || d.balance) - num(d.balance)), 0);
  const freeDate = sim.totalMonths ? monthAt(sim.totalMonths, sim.base) : null;
  const order = active.slice().sort((a, b) => (sim.months[a.id] || 9999) - (sim.months[b.id] || 9999));
  const done = debts.filter((d) => num(d.balance) <= 0.004);
  const front = order[0];
  const method = data.method || 'avalanche';

  return (
    <PlanPage
      onBack={onBack}
      title={`Debt payoff ${FLAG}`}
      sub={active.length ? (freeDate ? `Debt-free by ${monthYear(freeDate)}` : 'Not paid off at this rate yet') : 'You did it!'}
      right={<span style={{ fontSize: 12, fontWeight: 600, color: K.lilT, background: K.lil, borderRadius: 999, padding: '5px 10px' }}>{method === 'snowball' ? 'Snowball' : 'Avalanche'}</span>}
      testid="debt-payoff-page"
    >
      {reviewList.length > 0 && (
        <Card style={{ padding: '14px 16px 6px' }} testid="debt-review">
          <div className="flex items-center justify-between">
            <span style={{ fontSize: 15.5, fontWeight: 600, color: K.ink }}>Payments to review</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: K.pinkT, background: K.pink, borderRadius: 999, padding: '3px 9px' }}>{reviewList.length}</span>
          </div>
          {reviewList.map(({ d, due }, i) => {
            const amt = perPayment(sim, d);
            return (
              <div key={d.id + dayKey(due)} className="flex items-center" style={{ gap: 11, padding: '11px 0', borderTop: i ? `1px solid ${K.line}` : 'none', marginTop: i ? 0 : 6 }}>
                <EmojiSquare emoji={d.emoji || debtEmoji(d.name)} bg={tintAt(debts.indexOf(d)).bg} size={38} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: K.ink }}>{d.name} · {wholeMoney(amt)}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: K.soft }}>Due {shortDate(due)}</span>
                </span>
                <button type="button" onClick={() => setReview({ debt: d, amount: amt, due })} style={{ flexShrink: 0, border: 0, borderRadius: 999, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: K.btn, color: '#FFFFFF', fontFamily: 'inherit' }}>Review</button>
              </div>
            );
          })}
        </Card>
      )}

      {active.length > 0 ? (
        <Card>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: 12.5, color: K.soft }}>Debt-free by</span>
            {sim.stuck && <span style={{ fontSize: 11.5, fontWeight: 600, color: K.lilT, background: K.lil, borderRadius: 999, padding: '4px 9px' }}>Needs a bigger payment</span>}
          </div>
          <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.8px', color: K.ink, marginTop: 2 }}>{freeDate ? monthYear(freeDate) : 'Not at this rate'}</div>
          <PayChart history={data.history || []} path={sim.path} />
          <div className="grid grid-cols-3" style={{ gap: 8, marginTop: 12 }}>
            {[['Paid off', wholeMoney(paidOff), K.mintT], ['Left', wholeMoney(leftTotal), K.ink], ['Interest left', wholeMoney(sim.interest), K.ink]].map(([l, v, c]) => (
              <div key={l} style={{ background: K.field, borderRadius: 16, padding: '10px 11px', minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 11.5, color: K.soft }}>{l}</span>
                <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: c, marginTop: 2 }}>{v}</span>
              </div>
            ))}
          </div>
          {sim.stuck && <Help>At least one minimum doesn't cover its interest, so that debt keeps growing. Add a bigger extra, or check the minimum.</Help>}
        </Card>
      ) : (
        <Card style={{ textAlign: 'center', padding: '24px 18px' }}>
          <div style={{ fontSize: 40 }}>{'\u{1F389}'}</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: K.ink, marginTop: 6 }}>You're debt-free!</div>
          <p style={{ fontSize: 14, color: K.soft, marginTop: 6, lineHeight: 1.5 }}>Every debt in your plan is paid off. That's huge!</p>
        </Card>
      )}

      {active.length > 0 && (
        <>
          <div className="flex" style={{ gap: 4, background: '#FFFFFF', borderRadius: 999, padding: 4, marginTop: 14 }}>
            {['avalanche', 'snowball'].map((k) => (
              <button key={k} type="button" onClick={() => save({ method: k }, 'nohistory')} style={{ flex: 1, border: 0, borderRadius: 999, padding: '8px 0', fontSize: 12.5, fontWeight: 600, background: method === k ? K.btn : 'transparent', color: method === k ? '#FFFFFF' : K.soft, fontFamily: 'inherit' }}>{METHODS[k].title}</button>
            ))}
          </div>
          <Help>{METHODS[method].name}. When a debt is paid off, its minimum and your extra move to the next one. We'll check with you first.</Help>
          {/* w2_20: your monthly amount, with the extra you can change */}
          <Card style={{ padding: '14px 16px' }} testid="debt-extra">
            <div className="flex items-center justify-between" style={{ gap: 10 }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, color: K.soft }}>Toward your debts each month</span>
                <span style={{ display: 'block', fontSize: 20, fontWeight: 600, color: K.ink, marginTop: 2 }}>{wholeMoney(active.reduce((t, d) => t + monthlyMin(d), 0) + num(data.extra))}</span>
              </span>
              <button type="button" onClick={() => setExtraOpen(true)} style={{ flexShrink: 0, border: 0, borderRadius: 999, padding: '8px 14px', fontSize: 13, fontWeight: 600, background: K.lil, color: K.lilT, fontFamily: 'inherit' }}>Change extra</button>
            </div>
            <div className="flex justify-between" style={{ marginTop: 10, background: K.field, borderRadius: 14, padding: '9px 12px', fontSize: 13, color: K.soft }}>
              <span>Minimums <b style={{ fontWeight: 600, color: K.ink }}>{wholeMoney(active.reduce((t, d) => t + monthlyMin(d), 0))}</b></span>
              <span>Your extra <b style={{ fontWeight: 600, color: K.ink }}>{wholeMoney(num(data.extra))}</b></span>
            </div>
          </Card>
        </>
      )}

      <div style={{ marginTop: 6 }}>
        {order.map((d, i) => {
          const t = tintAt(debts.indexOf(d));
          const startB = num(d.start_balance || d.balance);
          const pct = startB > 0 ? ((startB - num(d.balance)) / startB) * 100 : 0;
          const when = sim.months[d.id] ? monthYear(monthAt(sim.months[d.id], sim.base)) : 'Not at this rate';
          const isFront = d === front;
          return (
            <button key={d.id} type="button" onClick={() => setPage(d.id)} className="flex items-center w-full text-left" style={{ gap: 12, background: '#FFFFFF', borderRadius: 22, padding: '12px 14px', marginTop: 10, border: isFront ? `2px solid ${t.bg}` : '2px solid transparent', fontFamily: 'inherit' }}>
              <span style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
                <svg width="48" height="48" viewBox="0 0 48 48" style={{ position: 'absolute', inset: 0 }}>
                  <circle cx="24" cy="24" r="21" fill="none" stroke={K.track} strokeWidth="4" />
                  {pct > 0.5 && <circle cx="24" cy="24" r="21" fill="none" stroke={t.bar} strokeWidth="4" strokeLinecap="round" strokeDasharray={`${(132 * Math.min(100, pct)) / 100} 132`} transform="rotate(-90 24 24)" />}
                </svg>
                <span className="flex items-center justify-center" style={{ position: 'absolute', inset: 0, fontSize: 20 }}>{d.emoji || debtEmoji(d.name)}</span>
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="flex items-center justify-between" style={{ gap: 8 }}>
                  <span className="truncate" style={{ fontSize: 15, fontWeight: 600, color: K.ink }}>{d.name}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: t.ink, flexShrink: 0 }}>{when}</span>
                </span>
                <span style={{ display: 'block', fontSize: 12.5, color: K.soft, marginTop: 2 }}>
                  {wholeMoney(num(d.balance))} left · {num(d.rate)}%{i === 0 ? ' · paying this one first' : ''}
                </span>
              </span>
            </button>
          );
        })}
        {done.map((d) => (
          <button key={d.id} type="button" onClick={() => setPage(d.id)} className="flex items-center w-full text-left" style={{ gap: 12, background: 'rgba(255,255,255,0.6)', borderRadius: 22, padding: '12px 14px', marginTop: 10, border: 0, fontFamily: 'inherit' }}>
            <EmojiSquare emoji={'✓'} bg={K.mint} size={40} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="truncate" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: K.ink }}>{d.name}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: K.mintT }}>Paid off{d.paid_on ? ' ' + monthYear(parseDay(d.paid_on)) : ''}</span>
            </span>
          </button>
        ))}
      </div>

      {active.length > 0 && <BlackButton onClick={() => setPage('whatif')}>What if I paid more?</BlackButton>}
      <QuietButton color={K.lilT} onClick={() => setAddOpen(true)}>+ Add a debt</QuietButton>
      {paidThisMonth.length > 0 && <Help>Paid this month: {paidThisMonth.map((x) => x.d.name).join(', ')}.</Help>}
      <Help>Dates are estimates. They update as you confirm payments and update balances.</Help>

      {review && <ReviewSheet review={review} onClose={() => setReview(null)} onConfirm={confirmPayment} onSkip={skipMonth} />}
      {extraOpen && (
        <ExtraSheet
          data={data}
          onClose={() => setExtraOpen(false)}
          onSave={(x) => { save({ extra: x }, 'nohistory'); setExtraOpen(false); }}
        />
      )}
      {addOpen && (
        <DebtSheet
          onClose={() => setAddOpen(false)}
          onSave={(d) => { save({ debts: [...debts, { ...d, id: newId(), start_balance: d.balance }] }); setAddOpen(false); }}
        />
      )}
      {party && (
        <PaidOff
          debt={debts.find((d) => d.id === party)}
          data={data}
          onChoose={(move) => { if (move !== null) save({ rollover: { ...(data.rollover || {}), [party]: move } }, 'nohistory'); setParty(null); }}
        />
      )}
    </PlanPage>
  );
}

/* ---------- chart: what you owe, done (solid) and planned (dashed) ---------- */
function PayChart({ history, path }) {
  const W = 300; const H = 120; const top = 8; const bot = 112;
  const actual = (history || []).map((h) => num(h.total));
  if (!actual.length && !path.length) return null;
  const now = actual.length ? actual[actual.length - 1] : (path[0] || 0);
  const future = [now, ...path];
  const max = Math.max(1, ...actual, ...future);
  const pastN = Math.max(1, actual.length);
  const total = pastN - 1 + future.length - 1 || 1;
  const x = (i) => (i / total) * W;
  const y = (v) => bot - (v / max) * (bot - top);
  const pastPts = actual.length > 1 ? actual.map((v, i) => [x(i), y(v)]) : [[x(0), y(now)]];
  const nowX = x(pastN - 1);
  const futPts = future.map((v, i) => [x(pastN - 1 + i), y(v)]);
  const line = (pts) => pts.map(([a, b], i) => (i ? 'L' : 'M') + a.toFixed(1) + ' ' + b.toFixed(1)).join(' ');
  return (
    <div style={{ marginTop: 10 }}>
      <svg viewBox={`-6 0 ${W + 12} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }}>
        <line x1="0" y1={bot} x2={W} y2={bot} stroke={K.track} />
        {pastPts.length > 1 && <path d={line(pastPts) + ` L${nowX} ${bot} L0 ${bot}Z`} fill={K.mint} opacity="0.7" />}
        <path d={line(futPts)} fill="none" stroke="#C9C2D8" strokeWidth="2.5" strokeDasharray="5 6" strokeLinecap="round" />
        {pastPts.length > 1 && <path d={line(pastPts)} fill="none" stroke="#5DB88A" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />}
        <circle cx={nowX} cy={y(now)} r="5" fill="#FFFFFF" stroke="#5DB88A" strokeWidth="3" />
      </svg>
      <div className="flex justify-between" style={{ marginTop: 4, fontSize: 11.5, color: K.soft }}>
        <span>{actual.length > 1 ? 'When you started' : 'Now'}</span>
        <span>Debt-free</span>
      </div>
    </div>
  );
}

/* ---------- reviewing a payment ---------- */
function ReviewSheet({ review, onClose, onConfirm, onSkip }) {
  const { debt } = review;
  const [amount, setAmount] = useState(toText(review.amount));
  const [date, setDate] = useState(dayKey(review.due && review.due <= new Date() ? review.due : new Date()));
  const split = splitPayment(debt.balance, debt.rate, num(amount));
  return (
    <PlanSheet title={`${debt.name} payment ${debt.emoji || debtEmoji(debt.name)}`} onClose={onClose}>
      <p style={{ margin: '0 4px', fontSize: 13.5, color: K.soft, lineHeight: 1.45 }}>Check the amount and the date, then confirm. Change them if you paid something different.</p>
      <div className="flex" style={{ gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}><Label>Amount paid</Label><MoneyInput value={amount} onChange={setAmount} /></div>
        <div style={{ flex: 1, minWidth: 0 }}><Label>Date</Label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} /></div>
      </div>
      <div style={{ background: K.field, borderRadius: 18, padding: '4px 14px', marginTop: 14 }}>
        {[['Interest this month', money(split.interest), K.ink], ['Off what you owe', money(split.principal), K.mintT], [`Left on your ${debt.name.toLowerCase()}`, money(split.left), K.ink]].map(([l, v, c], i) => (
          <div key={l} className="flex justify-between items-baseline" style={{ padding: '10px 0', borderTop: i ? `1px solid ${K.line}` : 'none' }}>
            <span style={{ fontSize: 14, color: K.soft }}>{l}</span><span style={{ fontSize: 15, fontWeight: 600, color: c }}>{v}</span>
          </div>
        ))}
      </div>
      <Help>Interest is charged first, and the rest comes off what you owe.</Help>
      <BlackButton disabled={num(amount) <= 0 || !date} onClick={() => onConfirm(debt, num(amount), date)}>Confirm payment</BlackButton>
      <QuietButton onClick={() => onSkip(debt)}>I didn't pay this month</QuietButton>
    </PlanSheet>
  );
}

/* ---------- adding or editing one debt ---------- */
function ScheduleFields({ v, set }) {
  const f = v.freq || 'monthly';
  const monthly = num(v.min) * perMonth(f);
  const low = num(v.balance) > 0 && num(v.min) > 0 && minTooLow({ balance: num(v.balance), rate: num(v.rate), min: monthly });
  const dayBox = (key, ph) => (
    <input type="text" inputMode="numeric" value={v[key] || ''} placeholder={ph} onChange={(e) => set({ [key]: e.target.value.replace(/[^0-9]/g, '').slice(0, 2) })} style={{ width: 40, border: 0, background: 'transparent', fontSize: 16, fontWeight: 600, color: K.ink, outline: 'none', fontFamily: 'inherit', padding: 0, textAlign: 'center' }} />
  );
  return (
    <>
      <Label>Interest rate (yearly)</Label>
      <span className="flex items-center" style={inputStyle}>
        <input type="text" inputMode="decimal" value={v.rate} placeholder="0" onChange={(e) => set({ rate: e.target.value.replace(/[^0-9.]/g, '') })} style={{ flex: 1, minWidth: 0, border: 0, background: 'transparent', fontSize: 16, fontWeight: 600, color: K.ink, outline: 'none', fontFamily: 'inherit', padding: 0 }} />
        <span style={{ fontSize: 16, color: '#B4AFC2' }}>%</span>
      </span>
      <Help>Called APR on most statements.</Help>
      <Label>How often do you pay?</Label>
      <div className="flex flex-wrap" style={{ gap: 7 }}>
        {FREQS.map(([k, l]) => <Chip key={k} on={f === k} onClick={() => set({ freq: k })}>{l}</Chip>)}
      </div>
      <Label>Minimum payment {eachWord(f)}</Label>
      <MoneyInput value={v.min} onChange={(x) => set({ min: x })} />
      <Help>{f === 'monthly'
        ? 'The least you have to pay each month. It\u2019s on your statement, often as \u201Cminimum payment due\u201D.'
        : `The least you have to pay each time.${num(v.min) > 0 ? ` That\u2019s about ${wholeMoney(monthly)} a month.` : ''}`}</Help>
      {low && <Note tone="pink" style={{ fontSize: 13 }}>This minimum doesn't cover the interest, so this debt would keep growing with the minimum alone. Your extra will help.</Note>}
      {f === 'monthly' && (
        <>
          <Label>Payment day</Label>
          <span className="flex items-center" style={{ ...inputStyle, gap: 8 }}>
            <span style={{ fontSize: 15, color: K.soft }}>The</span>{dayBox('pay_day', '1')}<span style={{ fontSize: 15, color: K.soft }}>of each month</span>
          </span>
        </>
      )}
      {f === 'semimonthly' && (
        <>
          <Label>Payment days</Label>
          <span className="flex items-center" style={{ ...inputStyle, gap: 6 }}>
            <span style={{ fontSize: 15, color: K.soft }}>The</span>{dayBox('pay_day', '1')}<span style={{ fontSize: 15, color: K.soft }}>and the</span>{dayBox('pay_day2', '15')}<span style={{ fontSize: 15, color: K.soft }}>of each month</span>
          </span>
        </>
      )}
      {(f === 'weekly' || f === 'biweekly') && (
        <>
          <Label>Next payment</Label>
          <input type="date" value={v.next_date || ''} onChange={(e) => set({ next_date: e.target.value })} style={inputStyle} />
          <Help>We'll count {f === 'weekly' ? 'every week' : 'every 2 weeks'} from this date.</Help>
        </>
      )}
      <Help>We'll ask you to review each payment around its day.</Help>
    </>
  );
}
function DebtFields({ v, set }) {
  return (
    <>
      <Label>What you owe today</Label>
      <MoneyInput value={v.balance} onChange={(x) => set({ balance: x })} />
      <Help>The balance on your latest statement or lender's app. Don't add the interest you'll pay in future, Worthly works that out. If you see a "total of payments left", that already includes future interest, so don't use it.</Help>
      <ScheduleFields v={v} set={set} />
    </>
  );
}
const debtError = (v) => {
  if (!String(v.name || '').trim()) return 'Add a name for this debt.';
  if (num(v.balance) <= 0) return 'Add what you owe today.';
  if (String(v.rate).trim() === '') return 'Add the interest rate. Put 0 if there isn\u2019t one.';
  if (num(v.min) <= 0) return 'Add the minimum payment.';
  const f = v.freq || 'monthly';
  const okDay = (x) => num(x) >= 1 && num(x) <= 31;
  if (f === 'monthly' && !okDay(v.pay_day)) return 'Add a payment day between 1 and 31.';
  if (f === 'semimonthly' && !(okDay(v.pay_day) && okDay(v.pay_day2))) return 'Add both payment days, between 1 and 31.';
  if ((f === 'weekly' || f === 'biweekly') && !v.next_date) return 'Add the date of your next payment.';
  return '';
};
const cleanDebt = (v) => {
  const f = v.freq || 'monthly';
  return {
    name: String(v.name).trim(), emoji: v.emoji || '', balance: r2(v.balance), rate: r2(v.rate), min: r2(v.min), freq: f,
    pay_day: f === 'monthly' || f === 'semimonthly' ? Math.round(num(v.pay_day)) : null,
    pay_day2: f === 'semimonthly' ? Math.round(num(v.pay_day2)) : null,
    next_date: f === 'weekly' || f === 'biweekly' ? v.next_date : null,
  };
};
const debtForm = (d) => (d
  ? { name: d.name, emoji: d.emoji || '', balance: toText(d.balance), rate: String(d.rate ?? ''), min: toText(d.min), freq: d.freq || 'monthly', pay_day: d.pay_day ? String(d.pay_day) : '', pay_day2: d.pay_day2 ? String(d.pay_day2) : '', next_date: d.next_date || '' }
  : { name: '', emoji: '', balance: '', rate: '', min: '', freq: 'monthly', pay_day: '', pay_day2: '', next_date: '' });

function DebtSheet({ debt, onClose, onSave, onDelete }) {
  const [v, setV] = useState(debtForm(debt));
  const [error, setError] = useState('');
  const [sure, setSure] = useState(false);
  const set = (p) => { setV((o) => ({ ...o, ...p })); setError(''); };
  return (
    <PlanSheet title={debt ? 'Edit debt' : 'Add a debt'} onClose={onClose}>
      <Label>Name</Label>
      <input type="text" value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="Credit card" style={inputStyle} />
      {debt
        ? (
          <>
            <ScheduleFields v={{ ...v, balance: toText(debt.balance) }} set={set} />
            <Help>To change what you owe, use Update balance on the debt's page.</Help>
          </>
        )
        : <DebtFields v={v} set={set} />}
      {error && <p style={{ margin: '10px 4px 0', fontSize: 13, color: K.pinkT }}>{error}</p>}
      <BlackButton onClick={() => {
        const e = debtError(debt ? { ...v, balance: debt.balance } : v);
        if (e) { setError(e); return; }
        const c = cleanDebt(debt ? { ...v, balance: debt.balance } : v);
        onSave(debt ? { ...debt, ...c } : c);
      }}>Save</BlackButton>
      {onDelete && (sure ? (
        <div className="flex" style={{ gap: 8, marginTop: 10 }}>
          <button type="button" onClick={() => setSure(false)} style={{ flex: 1, border: 0, borderRadius: 999, padding: 12, fontSize: 14, fontWeight: 600, background: K.field, color: K.soft, fontFamily: 'inherit' }}>Keep it</button>
          <button type="button" onClick={onDelete} style={{ flex: 1, border: 0, borderRadius: 999, padding: 12, fontSize: 14, fontWeight: 600, background: K.pink, color: K.pinkT, fontFamily: 'inherit' }}>Yes, remove</button>
        </div>
      ) : <QuietButton color={K.pinkT} onClick={() => setSure(true)}>Remove from my plan</QuietButton>)}
    </PlanSheet>
  );
}

/* ---------- one debt ---------- */
function DebtPage({ debt, index, data, sim, onBack, onSaveDebt, onDelete, onReview, reviewSheet }) {
  const [edit, setEdit] = useState(false);
  const [update, setUpdate] = useState(false);
  const [all, setAll] = useState(false);
  const t = tintAt(index);
  const startB = num(debt.start_balance || debt.balance);
  const left = num(debt.balance);
  const pct = startB > 0 ? ((startB - left) / startB) * 100 : 0;
  const pays = (data.payments || []).filter((p) => p.debt_id === debt.id).sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const isDone = left <= 0.004;
  const when = sim.months[debt.id] ? monthYear(monthAt(sim.months[debt.id], sim.base)) : null;
  const order = Object.keys(sim.months).filter((k) => sim.months[k]).sort((a, b) => sim.months[a] - sim.months[b]);
  const place = order.indexOf(String(debt.id));
  const placeWord = ['First', 'Second', 'Third', 'Fourth', 'Fifth'][place] || null;
  return (
    <PlanPage
      onBack={onBack}
      title={`${debt.name} ${debt.emoji || debtEmoji(debt.name)}`}
      sub={isDone ? `Paid off${debt.paid_on ? ' ' + monthYear(parseDay(debt.paid_on)) : ''}!` : when ? `${placeWord ? placeWord + ' to be paid off · ' : ''}by ${when}` : 'Not paid off at this rate yet'}
      right={<button type="button" onClick={() => setEdit(true)} style={{ border: 0, background: 'none', fontSize: 14, fontWeight: 600, color: K.soft, fontFamily: 'inherit' }}>Edit</button>}
      testid="debt-page"
    >
      <Card>
        <div className="flex justify-between"><span style={{ fontSize: 12.5, color: K.soft }}>Left to pay</span><span style={{ fontSize: 12.5, color: K.soft }}>Started at {wholeMoney(startB)}</span></div>
        <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.8px', color: K.ink, marginTop: 2 }}>{money(left)}</div>
        <Bar pct={pct} color={t.bar} height={10} style={{ marginTop: 10 }} />
        <div className="flex justify-between" style={{ marginTop: 8 }}>
          <span style={{ fontSize: 12.5, color: K.soft }}>{Math.round(pct)}% paid off</span>
          <span style={{ fontSize: 12.5, color: K.soft }}>{num(debt.rate)}% · {wholeMoney(num(debt.min))} {everyWord(debt.freq)}</span>
        </div>
      </Card>
      {!isDone && (
        <Card style={{ padding: '4px 16px' }}>
          {[
            ...((debt.freq || 'monthly') === 'monthly' ? [] : [['Each payment', wholeMoney(perPayment(sim, debt))]]),
            [(debt.freq || 'monthly') === 'monthly' ? 'This month' : 'About a month', wholeMoney(sim.firstMonthPay[debt.id] || monthlyMin(debt))],
            ['Payments', scheduleText(debt)],
            ['Interest still to come', wholeMoney(sim.interestBy[debt.id] || 0)],
            ['Plan', paymentStory(sim, debt.id)],
          ].map(([l, v], i) => (
            <div key={l} className="flex justify-between items-baseline" style={{ gap: 12, padding: '11px 0', borderTop: i ? `1px solid ${K.line}` : 'none' }}>
              <span style={{ fontSize: 14, color: K.soft, flexShrink: 0 }}>{l}</span><span style={{ fontSize: 14.5, fontWeight: 600, color: K.ink, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </Card>
      )}
      <div style={{ fontSize: 13, fontWeight: 600, color: K.soft, margin: '18px 4px 0' }}>Payments</div>
      <Card style={{ padding: '4px 16px', marginTop: 8 }}>
        {pays.length === 0 && <div style={{ fontSize: 14, color: K.soft, padding: '12px 0' }}>No payments confirmed yet.</div>}
        {(all ? pays : pays.slice(0, 4)).map((p, i) => (
          <div key={p.id} className="flex justify-between items-center" style={{ padding: '10px 0', borderTop: i ? `1px solid ${K.line}` : 'none' }}>
            <span><span style={{ display: 'block', fontSize: 14, color: K.ink }}>{shortDate(parseDay(p.date))}</span><span style={{ fontSize: 12, color: K.soft }}>{money(num(p.interest))} interest</span></span>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: K.ink }}>{money(num(p.amount))}</span>
          </div>
        ))}
        {pays.length > 4 && !all && <button type="button" onClick={() => setAll(true)} style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, borderTop: `1px solid ${K.line}`, background: 'none', padding: '11px 0', fontSize: 14, fontWeight: 600, color: K.lilT, fontFamily: 'inherit' }}>See all {pays.length} payments</button>}
      </Card>
      {!isDone && <BlackButton onClick={() => onReview(debt)}>Add a payment</BlackButton>}
      {!isDone && <QuietButton onClick={() => setUpdate(true)}>Update balance</QuietButton>}
      {!isDone && <Help>Use Update balance if your statement shows a different amount.</Help>}

      {edit && <DebtSheet debt={debt} onClose={() => setEdit(false)} onSave={(d) => { onSaveDebt(d); setEdit(false); }} onDelete={onDelete} />}
      {update && <UpdateSheet debt={debt} onClose={() => setUpdate(false)} onSave={(b) => { onSaveDebt({ ...debt, balance: b, ...(b <= 0.004 ? { paid_on: dayKey(new Date()) } : {}) }); setUpdate(false); }} />}
      {reviewSheet}
    </PlanPage>
  );
}

function UpdateSheet({ debt, onClose, onSave }) {
  const [b, setB] = useState('');
  const had = num(debt.balance);
  const diff = b === '' ? null : r2(had - num(b));
  return (
    <PlanSheet title="Update balance" onClose={onClose}>
      <p style={{ margin: '0 4px', fontSize: 13.5, color: K.soft, lineHeight: 1.45 }}>What your latest statement or lender's app says you owe, without future interest.</p>
      <Label>What you owe today</Label>
      <MoneyInput value={b} onChange={setB} placeholder={toText(had)} />
      {diff !== null && Math.abs(diff) >= 0.01 && (
        <Note tone={diff > 0 ? 'mint' : 'lil'}>
          {diff > 0
            ? `We had ${money(had)}, so you're ${money(diff)} ahead! Your dates will update.`
            : `We had ${money(had)}, so it's ${money(-diff)} more than we thought. Your dates will update.`}
        </Note>
      )}
      <BlackButton disabled={b === ''} onClick={() => onSave(r2(b))}>Save</BlackButton>
    </PlanSheet>
  );
}

/* ---------- changing your extra (w2_20) ---------- */
function ExtraSheet({ data, onClose, onSave }) {
  const [x, setX] = useState(toText(data.extra));
  const now = planFor(data);
  const next = planFor(data, { extra: num(x) });
  const mins = (data.debts || []).filter((d) => num(d.balance) > 0.004).reduce((t, d) => t + monthlyMin(d), 0);
  const nowDate = now.totalMonths ? monthAt(now.totalMonths, now.base) : null;
  const newDate = next.totalMonths ? monthAt(next.totalMonths, next.base) : null;
  const diff = now.totalMonths && next.totalMonths ? now.totalMonths - next.totalMonths : 0;
  return (
    <PlanSheet title="Your extra" onClose={onClose}>
      <p style={{ margin: '0 4px', fontSize: 13.5, color: K.soft, lineHeight: 1.45 }}>What you pay each month on top of your {wholeMoney(mins)} in minimums. It goes to the debt at the front of the line.</p>
      <Label>Extra each month</Label>
      <MoneyInput value={x} onChange={setX} placeholder="0" />
      <Help>However much you can, or nothing for now. You can change it any time.</Help>
      <Note tone={diff > 0 ? 'mint' : 'lil'}>
        That's <b style={{ fontWeight: 600 }}>{wholeMoney(mins + num(x))} a month</b> toward your debts.
        {newDate ? ` Debt-free by ${monthYear(newDate)}` : ' Not paid off at this rate yet.'}
        {newDate && nowDate && diff > 0 ? `, ${diff} month${diff === 1 ? '' : 's'} sooner.` : ''}
        {newDate && nowDate && diff < 0 ? `, ${-diff} month${diff === -1 ? '' : 's'} later.` : ''}
        {newDate && (!nowDate || diff === 0) ? '.' : ''}
      </Note>
      <BlackButton onClick={() => onSave(Math.round(num(x) * 100) / 100)}>Save</BlackButton>
    </PlanSheet>
  );
}

/* ---------- what if I paid more ---------- */
function WhatIf({ data, onBack, onSave }) {
  const [add, setAdd] = useState(100);
  const base = useMemo(() => planFor(data), [data]);
  const more = planFor(data, { extra: num(data.extra) + add });
  const max = Math.max(400, Math.ceil((num(data.extra) * 3) / 50) * 50);
  const baseDate = base.totalMonths ? monthAt(base.totalMonths, base.base) : null;
  const newDate = more.totalMonths ? monthAt(more.totalMonths, more.base) : null;
  const sooner = base.totalMonths && more.totalMonths ? base.totalMonths - more.totalMonths : null;
  const less = Math.max(0, base.interest - more.interest);
  const debts = (data.debts || []).filter((d) => num(d.balance) > 0.004);
  return (
    <PlanPage onBack={onBack} title="What if I paid more?" sub="Try an amount. Nothing changes until you save it." testid="debt-whatif">
      <Card>
        <div className="flex justify-between items-baseline"><span style={{ fontSize: 13, color: K.soft }}>Extra each month</span><span style={{ fontSize: 22, fontWeight: 600, color: K.ink }}>+{wholeMoney(add)}</span></div>
        <input type="range" min="0" max={max} step="10" value={add} onChange={(e) => setAdd(Number(e.target.value))} style={{ width: '100%', marginTop: 14, accentColor: K.lilDot }} />
        <div className="flex justify-between" style={{ fontSize: 12, color: K.soft }}><span>{wholeMoney(0)}</span><span>{wholeMoney(max)}</span></div>
      </Card>
      <div className="grid grid-cols-2" style={{ gap: 10, marginTop: 12 }}>
        <div style={{ background: K.mint, borderRadius: 20, padding: '13px 14px' }}>
          <span style={{ display: 'block', fontSize: 12, color: 'rgba(38,37,46,.62)' }}>Debt-free by</span>
          <span style={{ display: 'block', fontSize: 21, fontWeight: 600, color: K.mintT, marginTop: 3 }}>{newDate ? monthYear(newDate) : 'Not yet'}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'rgba(38,37,46,.62)', marginTop: 6 }}>{sooner > 0 ? `${sooner} month${sooner === 1 ? '' : 's'} sooner` : baseDate ? `Same as ${monthYear(baseDate)}` : ''}</span>
        </div>
        <div style={{ background: K.pink, borderRadius: 20, padding: '13px 14px' }}>
          <span style={{ display: 'block', fontSize: 12, color: 'rgba(38,37,46,.62)' }}>Less interest</span>
          <span style={{ display: 'block', fontSize: 21, fontWeight: 600, color: K.pinkT, marginTop: 3 }}>{wholeMoney(less)}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'rgba(38,37,46,.62)', marginTop: 6 }}>{wholeMoney(more.interest)} instead of {wholeMoney(base.interest)}</span>
        </div>
      </div>
      <Card style={{ padding: '4px 16px' }}>
        {debts.map((d, i) => {
          const a = base.months[d.id]; const b = more.months[d.id];
          const diff = a && b ? a - b : 0;
          return (
            <div key={d.id} className="flex justify-between items-baseline" style={{ padding: '11px 0', borderTop: i ? `1px solid ${K.line}` : 'none', gap: 10 }}>
              <span style={{ fontSize: 14, color: K.soft }}>{d.emoji || debtEmoji(d.name)} {d.name}</span>
              <span style={{ fontSize: 14.5, fontWeight: 600, color: K.ink }}>{b ? monthYear(monthAt(b, more.base)) : 'Not yet'}{diff > 0 && <span style={{ fontSize: 12, color: K.mintT, fontWeight: 600 }}> {diff} mo sooner</span>}</span>
            </div>
          );
        })}
      </Card>
      <BlackButton disabled={add <= 0} onClick={() => onSave(num(data.extra) + add)}>Add {wholeMoney(add)} to my extra</BlackButton>
      <QuietButton onClick={onBack}>Not now</QuietButton>
    </PlanPage>
  );
}

/* ---------- a debt paid off ---------- */
function PaidOff({ debt, data, onChoose }) {
  useEffect(() => {
    try { confetti({ particleCount: 110, spread: 78, origin: { y: 0.35 }, colors: ['#B9A6F0', '#9ED9B6', '#F2AFC8', '#A9C6F0', '#F3DE8E'], zIndex: 9999 }); } catch (e) { /* no confetti */ }
  }, []);
  if (!debt) return null;
  const rest = (data.debts || []).filter((d) => num(d.balance) > 0.004);
  const months = (() => {
    const s = data.started ? parseDay(data.started) : null;
    const e = debt.paid_on ? parseDay(debt.paid_on) : new Date();
    return s ? Math.max(1, (e.getFullYear() - s.getFullYear()) * 12 + e.getMonth() - s.getMonth() + 1) : null;
  })();
  const ask = rest.length > 0 && num(debt.min) > 0;
  const freedAmt = wholeMoney(monthlyMin(debt));
  const freedPhrase = (debt.freq || 'monthly') === 'monthly' ? `${freedAmt} minimum` : `minimum (about ${freedAmt} a month)`;
  const withMin = ask ? planFor(data, { rollover: { ...(data.rollover || {}), [debt.id]: true } }) : null;
  const without = ask ? planFor(data, { rollover: { ...(data.rollover || {}), [debt.id]: false } }) : null;
  const next = ask ? rest.slice().sort((a, b) => (withMin.months[a.id] || 9999) - (withMin.months[b.id] || 9999))[0] : null;
  const nextPay = next ? r2((withMin.firstMonthPay[next.id] || 0)) : 0;
  const extra = num(data.extra);
  return (
    <div className="fixed inset-0" style={{ zIndex: 80, background: K.page, overflowY: 'auto', paddingTop: 'calc(env(safe-area-inset-top, 0px) + 30px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 30px)' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '0 18px', textAlign: 'center' }}>
        <div className="flex items-center justify-center" style={{ width: 96, height: 96, borderRadius: 32, background: K.pink, fontSize: 44, margin: '0 auto' }}>{debt.emoji || debtEmoji(debt.name)}</div>
        <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.9px', color: K.ink, marginTop: 20 }}>{debt.name} paid off! {'\u{1F389}'}</div>
        <p style={{ fontSize: 15, color: K.soft, marginTop: 8 }}>
          {rest.length ? `That's one down and ${rest.length} to go!` : "That was your last one. You're debt-free!"}
        </p>
        {months && (
          <div style={{ background: '#FFFFFF', borderRadius: 22, padding: '12px 16px', marginTop: 18, textAlign: 'left' }} className="flex justify-between">
            <span style={{ fontSize: 14, color: K.soft }}>Paid off in</span><span style={{ fontSize: 15, fontWeight: 600, color: K.ink }}>{months} month{months === 1 ? '' : 's'}</span>
          </div>
        )}
        {ask ? (
          <div style={{ textAlign: 'left' }}>
            <div style={{ background: '#FFFFFF', borderRadius: 22, padding: '14px 16px', marginTop: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: K.ink }}>Your {debt.name.toLowerCase()}'s {freedPhrase} is free now</div>
              <p style={{ fontSize: 13, color: K.soft, lineHeight: 1.5, marginTop: 4 }}>
                {extra > 0 ? `Your ${wholeMoney(extra)} extra moves to your ${next.name.toLowerCase()} either way. ` : ''}Want the {freedAmt} to go there too?
              </p>
              {[['Move both', withMin.months[next.id]], [extra > 0 ? `Only my ${wholeMoney(extra)} extra` : 'Keep it for something else', without.months[next.id]]].map(([l, m], i) => (
                <div key={l} className="flex justify-between items-baseline" style={{ padding: '10px 0 2px', borderTop: i ? `1px solid ${K.line}` : 'none', marginTop: i ? 8 : 6 }}>
                  <span style={{ fontSize: 14, color: K.soft }}>{l}</span><span style={{ fontSize: 14.5, fontWeight: 600, color: K.ink }}>{next.name} {m ? monthYear(monthAt(m, withMin.base)) : 'later'}</span>
                </div>
              ))}
            </div>
            <BlackButton onClick={() => onChoose(true)}>Move both, {wholeMoney(nextPay)} a month</BlackButton>
            <QuietButton onClick={() => onChoose(false)}>{extra > 0 ? `Only move my ${wholeMoney(extra)} extra` : 'Keep it for something else'}</QuietButton>
          </div>
        ) : (
          <BlackButton onClick={() => onChoose(null)}>{rest.length ? 'Keep going' : 'Done'}</BlackButton>
        )}
      </div>
    </div>
  );
}

/* ---------- setting it up ---------- */
function Setup({ wealthDebts, onBack, onFinish }) {
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState(() => (wealthDebts || []).map((d) => ({ key: 'w' + d.id, wealth_id: d.id, name: d.name, emoji: d.emoji || '', on: false })));
  const [newName, setNewName] = useState('');
  const [details, setDetails] = useState({}); // key -> fields
  const [at, setAt] = useState(0);
  const [extra, setExtra] = useState('');
  const [method, setMethod] = useState('avalanche');
  const [error, setError] = useState('');
  const chosen = picked.filter((p) => p.on);
  const debtsNow = chosen.map((p) => {
    const f = details[p.key] || {};
    const c = cleanDebt({ ...f, name: p.name, emoji: p.emoji });
    return { ...c, id: p.key, balance: num(f.balance), rate: num(f.rate), min: num(f.min) };
  });
  const minTotal = debtsNow.reduce((s, d) => s + monthlyMin(d), 0);
  const simDebts = debtsNow.map((d) => ({ ...d, min: monthlyMin(d) }));
  const steps = 5;
  const back = () => { setError(''); if (step === 2 && at > 0) { setAt(at - 1); return; } if (step === 0) onBack(); else setStep(step - 1); };
  const bar = step > 0 && (
    <div className="flex" style={{ gap: 5, margin: '14px 4px 0' }}>
      {Array.from({ length: steps }, (_, i) => <span key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i < step ? K.lilDot : '#E3DFEC' }} />)}
    </div>
  );
  const stepLabel = step > 0 ? <span style={{ fontSize: 13, color: K.soft }}>Step {step} of {steps}</span> : null;

  if (step === 0) {
    return (
      <PlanPage key={`setup-${step}-${at}`} onBack={back} title={`Debt payoff ${FLAG}`} sub="See when you'll be debt-free, and how to get there sooner." testid="debt-setup">
        <Card style={{ padding: '6px 16px' }}>
          {[['\u{1F4CB}', K.lil, 'Your debts', 'What you owe on each, its rate and its minimum payment.'], ['➕', K.mint, 'Your extra', 'How much you can pay on top of the minimums each month.'], ['\u{1F3D4}️', K.pink, 'Your order', 'Avalanche or snowball: which debt your extra goes to first.']].map(([e, bg, t, s], i) => (
            <div key={t} className="flex" style={{ gap: 12, padding: '11px 0', borderTop: i ? `1px solid ${K.line}` : 'none' }}>
              <EmojiSquare emoji={e} bg={bg} size={38} />
              <span><span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: K.ink }}>{t}</span><span style={{ display: 'block', fontSize: 13, color: K.soft, lineHeight: 1.45, marginTop: 1 }}>{s}</span></span>
            </div>
          ))}
        </Card>
        <Note tone="blue">Have a recent statement or your lender's app handy. You'll need each debt's balance, interest rate and minimum payment.</Note>
        <BlackButton onClick={() => setStep(1)}>Let's start</BlackButton>
      </PlanPage>
    );
  }

  if (step === 1) {
    return (
      <PlanPage key={`setup-${step}-${at}`} onBack={back} right={stepLabel} title="Which debts?" sub={picked.length ? 'Pick from the debts on your Wealth page, or add one.' : 'Add each debt you want to pay off.'}>
        {bar}
        {picked.length > 0 && (
          <Card style={{ padding: '4px 16px' }}>
            {picked.map((p, i) => (
              <button key={p.key} type="button" onClick={() => { setPicked(picked.map((x) => (x.key === p.key ? { ...x, on: !x.on } : x))); setError(''); }} className="flex items-center w-full text-left" style={{ gap: 12, padding: '11px 0', background: 'none', border: 'none', borderTop: i ? `1px solid ${K.line}` : 'none', fontFamily: 'inherit' }}>
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 24, height: 24, borderRadius: 8, background: p.on ? K.lilDot : '#FFFFFF', boxShadow: p.on ? 'none' : 'inset 0 0 0 2px #D9D3E6', color: '#FFFFFF', fontSize: 13 }}>{p.on ? '✓' : ''}</span>
                <EmojiSquare emoji={p.emoji || debtEmoji(p.name)} bg={tintAt(i).bg} size={36} />
                <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: K.ink }}>{p.name}</span>
              </button>
            ))}
          </Card>
        )}
        <Help>Leave out anything you're not trying to pay off early, like a mortgage.</Help>
        <div className="flex" style={{ gap: 8, marginTop: 12 }}>
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={picked.length ? 'Add another debt' : 'e.g. Credit card'} style={{ ...inputStyle, background: '#FFFFFF' }} />
          <button type="button" onClick={() => { const nm = newName.trim(); if (!nm) return; setPicked([...picked, { key: 'n' + newId(), name: nm, emoji: '', on: true }]); setNewName(''); setError(''); }} style={{ flexShrink: 0, border: 0, borderRadius: 16, padding: '0 16px', fontSize: 14, fontWeight: 600, background: K.lil, color: K.lilT, fontFamily: 'inherit' }}>Add</button>
        </div>
        {error && <p style={{ margin: '10px 4px 0', fontSize: 13, color: K.pinkT }}>{error}</p>}
        <BlackButton onClick={() => { if (!chosen.length) { setError('Pick or add at least one debt.'); return; } setAt(0); setStep(2); }}>Next</BlackButton>
      </PlanPage>
    );
  }

  if (step === 2) {
    const p = chosen[Math.min(at, chosen.length - 1)];
    const f = details[p.key] || { balance: '', rate: '', min: '', freq: 'monthly', pay_day: '', pay_day2: '', next_date: '' };
    const set = (patch) => { setDetails({ ...details, [p.key]: { ...f, ...patch } }); setError(''); };
    return (
      <PlanPage key={`setup-${step}-${at}`} onBack={back} right={stepLabel} title={`${p.name} ${p.emoji || debtEmoji(p.name)}`} sub={`Debt ${at + 1} of ${chosen.length}`}>
        {bar}
        <DebtFields v={f} set={set} />
        {error && <p style={{ margin: '10px 4px 0', fontSize: 13, color: K.pinkT }}>{error}</p>}
        <BlackButton onClick={() => {
          const e = debtError({ ...f, name: p.name });
          if (e) { setError(e); return; }
          if (at < chosen.length - 1) setAt(at + 1); else setStep(3);
        }}>{at < chosen.length - 1 ? 'Next debt' : 'Next'}</BlackButton>
      </PlanPage>
    );
  }

  if (step === 3) {
    return (
      <PlanPage key={`setup-${step}-${at}`} onBack={back} right={stepLabel} title="Your extra" sub="Paying more than the minimums is what gets you there sooner.">
        {bar}
        <Card style={{ padding: '4px 16px' }}>
          {debtsNow.map((d, i) => (
            <div key={d.id} className="flex justify-between items-baseline" style={{ padding: '10px 0', borderTop: i ? `1px solid ${K.line}` : 'none' }}>
              <span style={{ fontSize: 14, color: K.soft }}>{d.emoji || debtEmoji(d.name)} {d.name}</span><span style={{ fontSize: 15, fontWeight: 600, color: K.ink, textAlign: 'right' }}>{money(d.min)}{(d.freq || 'monthly') !== 'monthly' && <span style={{ display: 'block', fontSize: 11.5, fontWeight: 500, color: K.soft }}>{everyWord(d.freq)}, about {wholeMoney(monthlyMin(d))} a month</span>}</span>
            </div>
          ))}
          <div className="flex justify-between items-baseline" style={{ padding: '10px 0', borderTop: `1px solid ${K.line}` }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: K.ink }}>Minimums each month</span><span style={{ fontSize: 15, fontWeight: 600, color: K.ink }}>{money(minTotal)}</span>
          </div>
        </Card>
        <Label>Extra on top, each month</Label>
        <MoneyInput value={extra} onChange={setExtra} />
        <Help>However much you can, or nothing for now. You can change it any time, and the plan updates.</Help>
        <Note>That's <b style={{ fontWeight: 600 }}>{money(minTotal + num(extra))} a month</b> toward your debts.</Note>
        <BlackButton onClick={() => setStep(4)}>Next</BlackButton>
      </PlanPage>
    );
  }

  const sims = {
    avalanche: simulate({ debts: simDebts, extra: num(extra), method: 'avalanche' }),
    snowball: simulate({ debts: simDebts, extra: num(extra), method: 'snowball' }),
  };
  const setupBase = planBase(debtsNow, dayKey(new Date()));
  sims.avalanche.base = setupBase;
  sims.snowball.base = setupBase;
  const cheaper = sims.avalanche.interest <= sims.snowball.interest ? 'avalanche' : 'snowball';

  if (step === 4) {
    return (
      <PlanPage key={`setup-${step}-${at}`} onBack={back} right={stepLabel} title="Which first?" sub="Your extra goes to one debt at a time. When it's paid off, that payment moves to the next one.">
        {bar}
        {['avalanche', 'snowball'].map((k) => {
          const M = METHODS[k]; const s = sims[k]; const on = method === k;
          return (
            <button key={k} type="button" onClick={() => setMethod(k)} className="block w-full text-left" style={{ background: '#FFFFFF', borderRadius: 22, padding: '15px 16px', marginTop: 12, border: 0, boxShadow: on ? `inset 0 0 0 2px ${K.lilBar}` : 'none', fontFamily: 'inherit' }}>
              <span className="flex items-start justify-between" style={{ gap: 10 }}>
                <span>
                  <span style={{ display: 'block', fontSize: 15.5, fontWeight: 600, color: K.ink }}>{M.title} {M.emoji}</span>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: K.lilT }}>{M.name}</span>
                </span>
                <span style={{ width: 22, height: 22, borderRadius: 999, flexShrink: 0, border: on ? `7px solid ${K.lilDot}` : '2px solid #D9D3E6', boxSizing: 'border-box' }} />
              </span>
              <span style={{ display: 'block', fontSize: 13, color: K.soft, lineHeight: 1.45, marginTop: 6 }}>{M.what}</span>
              <span className="flex justify-between" style={{ marginTop: 10, background: K.field, borderRadius: 14, padding: '10px 12px', fontSize: 13, color: K.ink }}>
                <span>Debt-free <b style={{ fontWeight: 600 }}>{s.totalMonths ? monthYear(monthAt(s.totalMonths, s.base)) : 'not yet'}</b></span>
                <span>Interest <b style={{ fontWeight: 600 }}>{wholeMoney(s.interest)}</b></span>
              </span>
              {cheaper === k && Math.abs(sims.avalanche.interest - sims.snowball.interest) >= 1 && (
                <span style={{ display: 'inline-block', marginTop: 10, fontSize: 11.5, fontWeight: 600, color: K.mintT, background: K.mint, borderRadius: 999, padding: '4px 10px' }}>Saves you the most</span>
              )}
            </button>
          );
        })}
        <Note tone="blue" style={{ fontSize: 13 }}>When a debt is paid off, its minimum is free. Both methods work by moving it, with your extra, to the next debt. We'll check with you each time before we do.</Note>
        <Help>You can switch methods any time.</Help>
        <BlackButton onClick={() => setStep(5)}>See my plan</BlackButton>
      </PlanPage>
    );
  }

  const s = sims[method];
  const ordered = debtsNow.slice().sort((a, b) => (s.months[a.id] || 9999) - (s.months[b.id] || 9999));
  return (
    <PlanPage key={`setup-${step}-${at}`} onBack={back} right={stepLabel} title={s.totalMonths ? `Debt-free by ${monthYear(monthAt(s.totalMonths, s.base))}! \u{1F389}` : 'Your plan'} sub={`With the ${method} method and ${money(minTotal + num(extra))} a month.`}>
      {bar}
      {s.stuck && <Note tone="pink">At this amount, at least one debt keeps growing. Try a bigger extra, or check the minimums.</Note>}
      {ordered.map((d, i) => (
        <div key={d.id} className="flex items-center" style={{ gap: 12, background: '#FFFFFF', borderRadius: 22, padding: '12px 14px', marginTop: 10 }}>
          <EmojiSquare emoji={d.emoji || debtEmoji(d.name)} bg={tintAt(i).bg} size={42} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="flex justify-between" style={{ gap: 8 }}>
              <span className="truncate" style={{ fontSize: 15, fontWeight: 600, color: K.ink }}>{d.name}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: tintAt(i).ink, flexShrink: 0 }}>{s.months[d.id] ? monthYear(monthAt(s.months[d.id], s.base)) : 'Not yet'}</span>
            </span>
            <span style={{ display: 'block', fontSize: 12.5, color: K.soft, marginTop: 2 }}>{paymentStory(s, d.id)}</span>
          </span>
        </div>
      ))}
      <Card style={{ padding: '12px 16px' }}>
        <div className="flex justify-between"><span style={{ fontSize: 14, color: K.soft }}>Total interest</span><span style={{ fontSize: 15, fontWeight: 600, color: K.ink }}>{wholeMoney(s.interest)}</span></div>
      </Card>
      <Help>Dates are estimates. They update as you confirm payments and update balances.</Help>
      <BlackButton onClick={() => onFinish({
        debts: debtsNow.map((d) => {
          const p = chosen.find((x) => x.key === d.id);
          return { id: newId(), wealth_id: p && p.wealth_id != null ? p.wealth_id : null, name: d.name, emoji: d.emoji || '', balance: r2(d.balance), start_balance: r2(d.balance), rate: r2(d.rate), min: r2(d.min), freq: d.freq || 'monthly', pay_day: d.pay_day, pay_day2: d.pay_day2, next_date: d.next_date };
        }),
        extra: r2(extra),
        method,
      })}>Done</BlackButton>
    </PlanPage>
  );
}
